import { formatFlightAmount } from 'constants/flightBooking';
import { formatNumber } from 'utils/currencyFormat';

// Flight revenue: period labels and the client-side CSV export.

// issuedCount and refundedCount count orders; ticketCount counts tickets.
// partnerTotal is the amount at issue: later paid seat changes and exchanges have their own
// columns below and are not counted here (WP-BE-REV-1). seatTotal and extrasTotal are an
// informational breakdown of partnerTotal (already inside it, at issue only) — net unchanged
// (ancillaries.md §3.3).
// refundedTotal is the money actually returned to the customer for the order itself, not the
// airline's quote (WP-BE-REV-2); serviceFeeRefunded is the part of that which came out of the
// fee, and net = serviceFee - serviceFeeRefunded. A row where the refund lands in a later
// period than the issue carries no serviceFee of its own, so its net reads negative — the fee
// given back, not a loss; only the summary (or a row where issue and refund share a period)
// reads as a fee kept (api-contract.md, "GET /flight-revenue").
export const REVENUE_COLUMNS = [
  { key: 'issuedCount', label: 'Đơn' },
  { key: 'ticketCount', label: 'Vé' },
  { key: 'partnerTotal', label: 'Tiền trả hãng', isMoney: true },
  { key: 'seatTotal', label: 'Phí chọn ghế', isMoney: true },
  { key: 'extrasTotal', label: 'Dịch vụ thêm', isMoney: true },
  { key: 'serviceFee', label: 'Phí dịch vụ Bookese', isMoney: true },
  { key: 'refundedCount', label: 'Đơn hoàn' },
  { key: 'refundedTotal', label: 'Tiền hoàn', isMoney: true },
  { key: 'serviceFeeRefunded', label: 'Phí dịch vụ hoàn', isMoney: true },
  { key: 'net', label: 'Doanh thu ròng', isMoney: true },
];

// Seat-change, exchange and extras-purchase money is a pass-through airline amount (not in
// net), counted in the period it was paid; exchangeRefundsDue is what the airline owes back,
// counted when applied. Same shape works for a revenue row or the summary object: both carry
// these fields.
export const REVENUE_CHANGE_COLUMNS = [
  {
    key: 'seatChange', label: 'Đổi ghế (số / phí)', countKey: 'seatChangeCount', feeKey: 'seatChangeFees',
  },
  {
    key: 'exchange', label: 'Đổi vé (số / phí)', countKey: 'exchangeCount', feeKey: 'exchangeFees',
  },
  { key: 'exchangeRefundsDue', label: 'Hãng hoàn đổi vé', isMoney: true },
  {
    key: 'extrasPurchase',
    label: 'Mua thêm dịch vụ (số / phí)',
    countKey: 'extrasPurchaseCount',
    feeKey: 'extrasPurchaseFees',
  },
];

export const REVENUE_CHANGE_NOTE = 'Phí ghế, phí đổi vé và phí mua thêm dịch vụ tính theo ngày '
  + 'khách thanh toán; Trả đối tác là số tại thời điểm xuất vé.';

// The same fields as flat, raw-number columns for the CSV (spreadsheet-summable), matching
// the table's new columns but split instead of combined into "count / fee" text.
export const REVENUE_CHANGE_CSV_COLUMNS = [
  { key: 'seatChangeCount', label: 'Đổi ghế (số)' },
  { key: 'seatChangeFees', label: 'Đổi ghế (phí)' },
  { key: 'exchangeCount', label: 'Đổi vé (số)' },
  { key: 'exchangeFees', label: 'Đổi vé (phí)' },
  { key: 'exchangeRefundsDue', label: 'Hãng hoàn đổi vé' },
  { key: 'extrasPurchaseCount', label: 'Mua thêm dịch vụ (số)' },
  { key: 'extrasPurchaseFees', label: 'Mua thêm dịch vụ (phí)' },
];

// "count / fee" for the paired seat-change and exchange columns; a plain amount otherwise.
// Missing data (e.g. the summary before the first load) reads as '-', like the other columns.
export const formatRevenueChangeCell = (record, column, currency) => {
  if (column.countKey) {
    const { [column.countKey]: count, [column.feeKey]: fee } = record;
    if (count === undefined && fee === undefined) return '-';
    return `${formatNumber(count || 0)} / ${formatFlightAmount(fee || 0, currency)}`;
  }
  return formatFlightAmount(record[column.key], currency);
};

// Excel reads a UTF-8 CSV as Vietnamese only when it starts with a byte order mark.
export const CSV_BOM = String.fromCharCode(0xfeff);

// BE sends YYYY-MM-DD for groupBy=day and YYYY-MM for groupBy=month.
export const formatRevenuePeriod = (period) => {
  const [year, month, day] = String(period || '').split('-');
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) return `${day}/${month}/${year}`;
  if (/^\d{4}-\d{2}$/.test(period)) return `${month}/${year}`;
  return period || '-';
};

export const REVENUE_RANGE_ERROR = 'Khoảng thời gian tối đa 1 năm';

// Mirrors the BE check on GET /flight-revenue: to not before from, and to before from + 1 year.
// An incomplete range has no error; the page simply waits for the second date.
export const getRevenueRangeError = (from, to) => {
  if (!from || !to) return '';
  const limit = new Date(`${from}T00:00:00Z`);
  limit.setUTCFullYear(limit.getUTCFullYear() + 1);
  return to < from || new Date(`${to}T00:00:00Z`) >= limit ? REVENUE_RANGE_ERROR : '';
};

const escapeCsv = (value) => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const toCsvLine = (cells) => cells.map(escapeCsv).join(',');

// Raw numbers, so the columns can be summed in a spreadsheet.
export const buildRevenueCsv = ({ rows, summary }) => {
  const columns = [...REVENUE_COLUMNS, ...REVENUE_CHANGE_CSV_COLUMNS];
  const lines = [
    toCsvLine(['Kỳ', 'Hãng', ...columns.map((column) => column.label)]),
    ...rows.map((row) => toCsvLine([
      formatRevenuePeriod(row.period),
      row.system,
      ...columns.map((column) => row[column.key]),
    ])),
    toCsvLine(['Tổng', '', ...columns.map((column) => summary[column.key])]),
  ];
  return CSV_BOM + lines.join('\r\n');
};

export const downloadCsv = (csv, fileName) => {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
